import React, { useCallback, useContext, useEffect, useState } from "react";
import { TableCell, TableHead, TableRow, TableContainer, Table, TableBody, Toolbar, Typography, TextField, Switch } from "@mui/material";
import { Done, Edit, Undo, Add, Cancel, Delete } from "@mui/icons-material";
import BasePriceApiService from "../../adapters/xhr/BasePriceApiService";
import TicketApiService from "../../adapters/xhr/TicketApiService";
import SnackbarUtils from "../../SnackbarUtils";
import { useTranslation } from "react-i18next";
import AutomaticApprovalApiService from "../../adapters/xhr/AutomaticApprovalApiService";
import { AllowedFileExtensions, AutomaticApprovalSettings, DataType, UserRoles } from "../../Constant";
import { MyDatePicker } from "../../components/my-date-picker/MyDatePicker";
import { formatDate, formatTime, getTrainStationOptions } from "../../Utils";
import MyContext from "../../contexts/MyContext";
import AdministrationApiService from "../../adapters/xhr/AdministrationApiService";
import { MyDropdown } from "../../components/my-dropdown/MyDropdown";
import { MyNumberField } from "../../components/my-number-field/MyNumberField";
import { MyTextField } from "../../components/my-text-field/MyTextField";
import { MyDateTimePicker } from "../../components/my-date-time-picker/MyDateTimePicker";
import { MySelect } from "../../components/my-select/MySelect";
import authService from "../../components/api-authorization/AuthorizeService";

export const SettingsPage = () => {
  const { t } = useTranslation();
  const readOnly = authService._user.profile.role == UserRoles.CaseManager;
  const [automaticApprovalSettings, setAutomaticApprovalSettings] = useState(null);
  const [automaticApprovalSettingsPrevious, setAutomaticApprovalSettingsPrevious] = useState([]);
  const [basePriceRows, setBasePriceRows] = useState(null);
  const [basePricePrevious, setBasePricePrevious] = useState([]);
  const [ticketRows, setTicketRows] = useState(null);
  const [ticketPrevious, setTicketPrevious] = useState([]);
  const [isAddTicketMode, setIsAddTicketMode] = useState(false);
  const [isAddBasePriceMode, setIsAddBasePriceMode] = useState(false);
  const [isAddTrainsToRemoveMode, setIsAddTrainsToRemoveMode] = useState(false);
  const [isAddStationsToRemoveMode, setIsAddStationsToRemoveMode] = useState(false);
  const [isAddPeriodsMode, setIsAddPeriodsMode] = useState(false);
  const [autoApproval, setAutoApproval] = useState(true);
  const myContext = useContext(MyContext);
  const basePriceColumns = [
    { id: "Year", label: t("year"), minWidth: 400, width: 400, editable: false },
    { id: "Value", label: t("value"), minWidth: 400, width: 400, editable: true },
    { id: "Actions", label: t("actions"), minWidth: 170, hidden: readOnly },
  ];
  const ticketColumns = [
    { id: "Title", label: t("title"), minWidth: 400, width: 400, editable: true },
    { id: "Rule", label: t("rule"), minWidth: 400, width: 400, editable: true },
    { id: "Actions", label: t("actions"), minWidth: 170, hidden: readOnly },
  ];
  const autoApprovalColumns = [
    { id: "Title", label: t("title"), minWidth: 400, width: 400, editable: false },
    { id: "Value", label: t("value"), minWidth: 400, width: 400, editable: true },
    { id: "Actions", label: t("actions"), minWidth: 170, hidden: readOnly },
  ];
  const trainsToRemoveColumns = [
    { id: "Value", label: t("train_number"), minWidth: 400, width: 400, editable: true, type: "number" },
    { id: "FromDate", label: t("from_date"), minWidth: 400, width: 400, editable: true, type: "date", format: formatDate },
    { id: "ToDate", label: t("to_date"), minWidth: 400, width: 400, editable: true, type: "date", format: formatDate },
    { id: "Actions", label: t("actions"), minWidth: 100, hidden: readOnly },
  ];

  const getStationName = (code) => {
    return myContext.trainStations?.find((item) => item.code === code)?.name;
  };

  const stationsToRemoveColumns = [
    { id: "Value", label: t("station"), minWidth: 400, width: 400, editable: true, format: getStationName },
    { id: "FromDate", label: t("from_date"), minWidth: 400, width: 400, editable: true, type: "date", format: formatDate },
    { id: "ToDate", label: t("to_date"), minWidth: 400, width: 400, editable: true, type: "date", format: formatDate },
    { id: "Actions", label: t("actions"), minWidth: 100, hidden: readOnly },
  ];

  const periodsColumns = [
    // { id: "Value", label: t("station"), minWidth: 400, width: 400, editable: true, format: getStationName },
    {
      id: "FromDate",
      label: t("from_date"),
      minWidth: 400,
      width: 400,
      editable: true,
      type: "date",
      format: (value) => {
        return formatDate(value) + " " + formatTime(value);
      },
    },
    {
      id: "ToDate",
      label: t("to_date"),
      minWidth: 400,
      width: 400,
      editable: true,
      type: "date",
      format: (value) => {
        return formatDate(value) + " " + formatTime(value);
      },
    },
    { id: "Actions", label: t("actions"), minWidth: 100, hidden: readOnly },
  ];

  const fetchTrainStations = useCallback(async () => {
    let response = await AdministrationApiService.getTrainStations();
    if (response.isSuccess) {
      myContext.changeTrainStations(response.data);
    }
  }, []);

  useEffect(() => {
    if (!myContext.trainStations || myContext.trainStations.length === 0) fetchTrainStations();
  }, [fetchTrainStations]);

  const onToggleBasePriceEditMode = (id) => {
    setBasePriceRows((state) => {
      return basePricePrevious.map((row) => {
        if (row.id === id) {
          return { ...row, isEditMode: !row.isEditMode };
        }
        return row;
      });
    });
  };

  const onToggleTicketEditMode = (id) => {
    setTicketRows((state) => {
      return ticketPrevious.map((row) => {
        if (row.id === id) {
          return { ...row, isEditMode: !row.isEditMode };
        }
        return row;
      });
    });
  };

  const onToggleSettingsEditMode = (id) => {
    setAutomaticApprovalSettings((state) => {
      return automaticApprovalSettingsPrevious.map((row) => {
        if (row.id === id) {
          return { ...row, isEditMode: !row.isEditMode };
        }
        return row;
      });
    });
  };

  const onToggleTrainNumbersToRemoveAddMode = () => {
    setIsAddTrainsToRemoveMode(true);
    setIsAddStationsToRemoveMode(false);
    setIsAddPeriodsMode(false);
    setAutomaticApprovalSettings([
      {
        id: "0",
        Key: AutomaticApprovalSettings.TrainNumbersToRemove,
        Title: t("train_numbers_to_remove"),
        Value: "",
        FromDate: new Date(),
        ToDate: new Date(),
        isAddMode: true,
        DataType: DataType.Int,
      },
      ...automaticApprovalSettingsPrevious,
    ]);
  };

  const onToggleStationsToRemoveAddMode = () => {
    setIsAddStationsToRemoveMode(true);
    setIsAddTrainsToRemoveMode(false);
    setIsAddPeriodsMode(false);
    setAutomaticApprovalSettings([
      {
        id: "0",
        Key: AutomaticApprovalSettings.StationsToRemove,
        Title: t("stations_to_remove"),
        Value: "",
        FromDate: new Date(),
        ToDate: new Date(),
        isAddMode: true,
      },
      ...automaticApprovalSettingsPrevious,
    ]);
  };

  const onTogglePeriodsAddMode = () => {
    setIsAddPeriodsMode(true);
    setIsAddTrainsToRemoveMode(false);
    setIsAddStationsToRemoveMode(false);
    setAutomaticApprovalSettings([
      {
        id: "0",
        Key: AutomaticApprovalSettings.Periods,
        Title: t("inactive_periods"),
        Value: "false",
        FromDate: new Date(),
        ToDate: new Date(),
        isAddMode: true,
      },
      ...automaticApprovalSettingsPrevious,
    ]);
  };

  const onToggleAddMode = (active) => {
    setIsAddBasePriceMode(active);
    if (active) setBasePriceRows([{ id: "0", Year: "", Value: "", isAddMode: true }, ...basePricePrevious]);
    else setBasePriceRows(basePricePrevious);
  };

  const onToggleAddTicketMode = (active) => {
    let defaultRule =
      '[{"percentage":25,"reimbursementSEK":10},{"percentage":50,"reimbursementSEK":10},{"percentage":75,"reimbursementSEK":10},{"percentage":100,"reimbursementSEK":10}]';
    setIsAddTicketMode(active);
    if (active) setTicketRows([{ id: "0", Title: "", Rule: defaultRule, isAddMode: true }, ...ticketPrevious]);
    else setTicketRows(ticketPrevious);
  };

  const onSaveBasePrice = async (id) => {
    let item = basePriceRows.find((item) => item.id === id);
    let payload = {
      year: Number(item.Year),
      value: Number(item.Value),
    };
    let response = await BasePriceApiService.update(item.Year, payload);
    if (response && response.isSuccess) {
      await fetchBasePrices();
    } else {
      SnackbarUtils.error(t(t(response.errorMessage)));
      onRevert(id);
    }
  };

  const onSaveTicket = async (id) => {
    let item = ticketRows.find((item) => item.id === id);
    if (ticketRows.find((x) => x.id != item.id && x.Title.trim() == item.Title.trim()) != null) {
      SnackbarUtils.error(t("ticket_with_title_existed", { title: item.Title }));
    } else {
      let item = ticketRows.find((item) => item.id === id);
      let payload = {
        id: id,
        title: item.Title,
        rule: item.Rule,
        enabled: item.Enabled,
      };
      let response = await TicketApiService.update(id, payload);
      if (response && response.isSuccess) {
        await fetchTickets();
      } else {
        SnackbarUtils.error(t("error"));
        onRevert(id);
      }
    }
  };

  const onSaveAutomaticApproval = async (id) => {
    let item = automaticApprovalSettings.find((item) => item.id === id);
    if (!(await onChangeAutoApprovalSettings(item.Key, item.Value, item.FromDate, item.ToDate))) {
      onRevertAutomaticApproval(id);
    }
  };

  const onAddAutomaticApproval = async (id) => {
    let item = automaticApprovalSettings.find((item) => item.id === id);
    await onAddAutoApprovalSettings(item.Key, item.Value, item.FromDate, item.ToDate);
  };

  const onDeleteBasePrice = async (id) => {
    let item = basePriceRows.find((item) => item.id === id);
    const response = await BasePriceApiService.deleteByYear(item.Year);
    if (response?.isSuccess) {
      await fetchBasePrices();
    } else {
      SnackbarUtils.error(t(response.errorMessage));
    }
  };

  const onDeleteTicket = async (id) => {
    const response = await TicketApiService.deleteById(id);
    if (response?.isSuccess) {
      await fetchTickets();
    } else {
      SnackbarUtils.error(t("error"));
    }
  };

  const onDeleteAutoApprovalSetting = async (id) => {
    let item = automaticApprovalSettings.find((item) => item.id === id);
    const response = await AutomaticApprovalApiService.deleteByKey(item.Key);
    if (response?.isSuccess) {
      await fetchAutomaticApprovalSettings();
    } else {
      SnackbarUtils.error(t(response.errorMessage));
    }
  };

  const onAddBasePrice = async (id) => {
    let item = basePriceRows.find((item) => item.id === id);
    if (basePriceRows.find((x) => x.id != item.id && x.Year == item.Year) != null) {
      SnackbarUtils.error(t("base_price_for_year_existed", { year: item.Year }));
    } else {
      if (item.Year.trim() !== "" && item.Value.trim() !== "") {
        let payload = {
          year: Number(item.Year),
          value: Number(item.Value),
        };
        let response = await BasePriceApiService.add(payload);
        if (response?.isSuccess) {
          await fetchBasePrices();
        } else {
          SnackbarUtils.error(t(response.errorMessage));
        }
      }
    }
  };

  const onAddTicket = async (id) => {
    let item = ticketRows.find((item) => item.id === id);
    if (ticketRows.find((x) => x.id != item.id && x.Title.trim() == item.Title.trim()) != null) {
      SnackbarUtils.error(t("ticket_with_title_existed", { title: item.Title }));
    } else {
      if (item.Title.trim() !== "" && item.Rule.trim() !== "") {
        let payload = {
          title: item.Title,
          rule: item.Rule,
        };
        let response = await TicketApiService.add(payload);
        if (response?.isSuccess) {
          await fetchTickets();
        } else {
          SnackbarUtils.error(t("error"));
        }
      }
    }
  };

  useEffect(() => {
    if (basePriceRows === null) fetchBasePrices();
  }, [basePriceRows]);

  useEffect(() => {
    if (ticketRows === null) fetchTickets();
  }, [ticketRows]);

  useEffect(() => {
    if (automaticApprovalSettings === null) fetchAutomaticApprovalSettings();
  }, [automaticApprovalSettings]);

  const fetchBasePrices = async () => {
    let response = await BasePriceApiService.getAll();
    if (response?.isSuccess) {
      let data = response.data
        .map((option, key) => {
          let rObj = {};
          rObj["id"] = option.id;
          rObj["Year"] = option.year;
          rObj["Value"] = option.value;
          return rObj;
        })
        .sort((a, b) => b.Year - a.Year);
      setBasePriceRows(data);
      setBasePricePrevious(data);
      setIsAddBasePriceMode(false);
    }
  };

  const fetchTickets = async () => {
    let response = await TicketApiService.getAll();
    if (response?.isSuccess) {
      let data = response.data.map((option, key) => {
        let rObj = {};
        rObj["id"] = option.id;
        rObj["Title"] = option.title;
        rObj["Enabled"] = option.enabled;
        rObj["Rule"] = JSON.stringify(option.rules);
        return rObj;
      });
      setTicketRows(data);
      setTicketPrevious(data);
      setIsAddTicketMode(false);
    }
  };

  const fetchAutomaticApprovalSettings = async () => {
    let response = await AutomaticApprovalApiService.getAll();
    if (response?.isSuccess) {
      let autoApproval = response.data.find((item) => item.key === "Enable");
      if (!!autoApproval) setAutoApproval(autoApproval.value?.toLowerCase() === "true" ? true : false);
      let data = response.data.map((item) => {
        let rObj = {};
        rObj["id"] = item.id;
        rObj["Key"] = item.key;
        switch (item.key) {
          case AutomaticApprovalSettings.ShortTripValidDays:
            rObj["Title"] = t("short_trip_valid_days");
            break;
          case AutomaticApprovalSettings.LongTripValidDays:
            rObj["Title"] = t("long_trip_valid_days");
            break;
          case AutomaticApprovalSettings.LongShortTripKmThreshold:
            rObj["Title"] = t("long_short_trip_km_threshold");
            break;
          case AutomaticApprovalSettings.DelayMinForLongTrip:
            rObj["Title"] = t("delay_min_for_long_trip");
            break;
          case AutomaticApprovalSettings.DelayMinForShortTrip:
            rObj["Title"] = t("delay_min_for_short_trip");
            break;
          case AutomaticApprovalSettings.TotalCostSEKMax:
            rObj["Title"] = t("total_cost_max");
            break;
          case AutomaticApprovalSettings.TransportationCostSEKMax:
            rObj["Title"] = t("transportation_cost_max");
            break;
          case AutomaticApprovalSettings.TrainNumbersToRemove:
            rObj["Title"] = t("train_numbers_to_remove");
            break;
          case AutomaticApprovalSettings.StationsToRemove:
            rObj["Title"] = t("stations_to_remove");
            break;
          case AutomaticApprovalSettings.CompensationForOwnCar:
            rObj["Title"] = t("compensation_for_own_car");
            break;
          case AutomaticApprovalSettings.Level1CompensationForLongTrip:
            rObj["Title"] = t("level1_compensation_for_long_trip");
            break;
          case AutomaticApprovalSettings.Level1SizeCompensationForLongTrip:
            rObj["Title"] = t("level1_size_compensation_for_long_trip");
            break;
          case AutomaticApprovalSettings.Level2CompensationForLongTrip:
            rObj["Title"] = t("level2_compensation_for_long_trip");
            break;
          case AutomaticApprovalSettings.Level2SizeCompensationForLongTrip:
            rObj["Title"] = t("level2_size_compensation_for_long_trip");
            break;
          case AutomaticApprovalSettings.Level1CompensationForShortTrip:
            rObj["Title"] = t("level1_compensation_for_short_trip");
            break;
          case AutomaticApprovalSettings.Level1SizeCompensationForShortTrip:
            rObj["Title"] = t("level1_size_compensation_for_short_trip");
            break;
          case AutomaticApprovalSettings.Level2CompensationForShortTrip:
            rObj["Title"] = t("level2_compensation_for_short_trip");
            break;
          case AutomaticApprovalSettings.Level2SizeCompensationForShortTrip:
            rObj["Title"] = t("level2_size_compensation_for_short_trip");
            break;
          case AutomaticApprovalSettings.Level3CompensationForShortTrip:
            rObj["Title"] = t("level3_compensation_for_short_trip");
            break;
          case AutomaticApprovalSettings.Level3SizeCompensationForShortTrip:
            rObj["Title"] = t("level3_size_compensation_for_short_trip");
            break;
          case AutomaticApprovalSettings.MaxClaimsIn24Hours:
            rObj["Title"] = t("max_claims_in_24_hours");
            break;
          case AutomaticApprovalSettings.MaxClaimsDuring7Days:
            rObj["Title"] = t("max_claims_during_7_days");
            break;
          case AutomaticApprovalSettings.MaxClaimsDuring30Days:
            rObj["Title"] = t("max_claims_during_30_days");
            break;
          case AutomaticApprovalSettings.MaxOpenClaims:
            rObj["Title"] = t("max_open_claims");
            break;
          case AutomaticApprovalSettings.MaxMoneyToApprove:
            rObj["Title"] = t("max_money_to_approve");
            break;
          case AutomaticApprovalSettings.BasePriceFor30dayTicket:
            rObj["Title"] = t("base_price_for_30_day_ticket");
            break;
          case AutomaticApprovalSettings.BasePriceFor90dayTicket:
            rObj["Title"] = t("base_price_for_90_day_ticket");
            break;
          case AutomaticApprovalSettings.BasePriceFor360dayTicket:
            rObj["Title"] = t("base_price_for_360_day_ticket");
            break;
          case AutomaticApprovalSettings.MaxClaimsOnCommuterTicketIn24Hours:
            rObj["Title"] = t("max_claims_on_commuter_ticket_in_24_hours");
            break;
          case AutomaticApprovalSettings.MaxClaimsPerPeriodTicketAndTrip:
            rObj["Title"] = t("max_claims_per_period_ticket_and_trip");
            break;
          case AutomaticApprovalSettings.MaxClaimsOnSingleTicketPerTrip:
            rObj["Title"] = t("max_claims_on_single_ticket_per_trip");
            break;
          case AutomaticApprovalSettings.AutomaticRejectionIsEnabled:
            rObj["Title"] = t("automatic_rejection_is_enabled");
            break;
          case AutomaticApprovalSettings.AutomaticApprovalIsEnabled:
            rObj["Title"] = t("automatic_approval_is_enabled");
            break;
          case AutomaticApprovalSettings.SingleTicketAutomaticProcessingIsEnabled:
            rObj["Title"] = t("single_ticket_automatic_processing");
            break;
          case AutomaticApprovalSettings.CommuterTicketAutomaticProcessingIsEnabled:
            rObj["Title"] = t("commuter_ticket_automatic_processing");
            break;
          case AutomaticApprovalSettings.AllowedFileExtension:
            rObj["Title"] = t("allowed_file_extension");
            break;
          case AutomaticApprovalSettings.HoursUntilClaimsBeingProcessed:
            rObj["Title"] = t("hours_until_claims_being_processed");
            break;
          case AutomaticApprovalSettings.MonthToDeleteSensitiveData:
            rObj["Title"] = t("month_to_delete_sensitive_data");
            break;
          default:
            rObj["Title"] = item.key;
            break;
        }
        rObj["Value"] = item.value;
        rObj["FromDate"] = item.fromDate;
        rObj["DataType"] = item.dataType;
        rObj["ToDate"] = item.toDate;
        return rObj;
      });
      data = data
        .filter((item) => item.Key !== AutomaticApprovalSettings.Enable)
        .sort(function (a, b) {
          // order by title
          var nameA = a.Title.toUpperCase(); // ignore upper and lowercase
          var nameB = b.Title.toUpperCase(); // ignore upper and lowercase
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          // names must be equal
          return 0;
        });
      setAutomaticApprovalSettings(data);
      setAutomaticApprovalSettingsPrevious(data);
    }
  };

  const onChangeBasePirce = (e, row) => {
    const value = e.target.value;
    if (value < 0) {
      return;
    }
    const name = e.target.name;
    const { id } = row;
    const newRows = basePriceRows.map((row) => {
      if (row.id === id) {
        return { ...row, [name]: value };
      }
      return row;
    });
    setBasePriceRows(newRows);
  };

  const onChangeTicketRule = (id, name, rule, index, reimbursementSEK) => {
    var value = JSON.parse(rule);
    value[index].reimbursementSEK = Number(reimbursementSEK);
    onChangeTicket(id, name, JSON.stringify(value));
  };

  const onChangeTicket = (id, name, value) => {
    const newRows = ticketRows.map((row) => {
      if (row.id === id) {
        return { ...row, [name]: value };
      }
      return row;
    });
    setTicketRows(newRows);
  };

  const onChangeApprovalSettingRow = (value, name, row) => {
    const { id, Key } = row;
    if (name === "Value" && (row.DataType === DataType.Int || row.DataType === DataType.Decimal)) {
      if (Number(value) < 0) {
        return;
      } else if (row.DataType === DataType.Int && !Number.isInteger(Number(value))) {
        return;
      } else if (Key === AutomaticApprovalSettings.ShortTripValidDays || Key === AutomaticApprovalSettings.LongTripValidDays) {
        if (Number(value) > AutomaticApprovalSettings.LongShortTripValidDaysMax) {
          return;
        }
      }
    }
    const newRows = automaticApprovalSettings.map((row) => {
      if (row.id === id) {
        return { ...row, [name]: value };
      }
      return row;
    });
    setAutomaticApprovalSettings(newRows);
  };

  const onChangeAutoApprovalSettings = async (key, value, fromDate, toDate) => {
    if (!value) return false;
    if (key.startsWith(AutomaticApprovalSettings.StationsToRemove)) value = value?.code ? value.code : value;
    let payload = { key: key, value: `${value}` };
    if (key.startsWith(AutomaticApprovalSettings.StationsToRemove) || key.startsWith(AutomaticApprovalSettings.TrainNumbersToRemove)) {
      if (!!fromDate && !!toDate) {
        let _fromDate = new Date(fromDate);
        let _toDate = new Date(toDate);
        _fromDate.setHours(0, 0, 0, 0);
        _toDate.setHours(0, 0, 0, 0);
        if (_fromDate.getTime() > _toDate.getTime()) return false;
        else {
          payload.fromDate = formatDate(fromDate);
          payload.toDate = formatDate(toDate);
        }
      } else return false;
    } else if (key.startsWith(AutomaticApprovalSettings.Periods)) {
      if (!!fromDate && !!toDate) {
        let _fromDate = new Date(fromDate);
        let _toDate = new Date(toDate);
        _fromDate.setSeconds(0);
        _toDate.setSeconds(0);
        if (_fromDate.getTime() > _toDate.getTime()) return false;
        else {
          payload.fromDate = formatDate(fromDate) + "T" + formatTime(fromDate, true);
          payload.toDate = formatDate(toDate) + "T" + formatTime(toDate, true);
        }
      } else return false;
    }
    const response = await AutomaticApprovalApiService.update(key, payload);
    if (!response || !response.isSuccess) {
      SnackbarUtils.error(t(response.errorMessage));
      return false;
    }
    await fetchAutomaticApprovalSettings();
    setIsAddTrainsToRemoveMode(false);
    setIsAddStationsToRemoveMode(false);
    setIsAddPeriodsMode(false);
    return true;
  };

  const onAddAutoApprovalSettings = async (row) => {
    let { Key, Value, FromDate, ToDate } = row;
    if (!Value) return;
    if (Key.startsWith(AutomaticApprovalSettings.StationsToRemove)) Value = Value?.code ? Value.code : Value;
    let payload = { key: Key, value: `${Value}` };
    if (Key.startsWith(AutomaticApprovalSettings.StationsToRemove) || Key.startsWith(AutomaticApprovalSettings.TrainNumbersToRemove)) {
      if (!!FromDate && !!ToDate) {
        let _fromDate = new Date(FromDate);
        let _toDate = new Date(ToDate);
        _fromDate.setHours(0, 0, 0, 0);
        _toDate.setHours(0, 0, 0, 0);
        if (_fromDate.getTime() > _toDate.getTime()) return;
        else {
          payload.fromDate = formatDate(FromDate);
          payload.toDate = formatDate(ToDate);
        }
      } else return;
    } else if (Key.startsWith(AutomaticApprovalSettings.Periods)) {
      if (!!FromDate && !!ToDate) {
        let _fromDate = new Date(FromDate);
        let _toDate = new Date(ToDate);
        _fromDate.setSeconds(0);
        _toDate.setSeconds(0);
        if (_fromDate.getTime() > _toDate.getTime()) return;
        else {
          payload.fromDate = formatDate(FromDate) + "T" + formatTime(FromDate, true);
          payload.toDate = formatDate(ToDate) + "T" + formatTime(ToDate, true);
        }
      } else return;
    }
    const response = await AutomaticApprovalApiService.add(payload);

    if (!response.isSuccess) {
      SnackbarUtils.error(t(response.errorMessage));
      return false;
    }

    await fetchAutomaticApprovalSettings();
    setIsAddTrainsToRemoveMode(false);
    setIsAddStationsToRemoveMode(false);
    setIsAddPeriodsMode(false);
  };

  const onRevert = (id) => {
    setBasePriceRows(basePricePrevious);
    setTicketRows(ticketPrevious);
  };

  const onRevertAutomaticApproval = (id) => {
    setIsAddTrainsToRemoveMode(false);
    setIsAddStationsToRemoveMode(false);
    setIsAddPeriodsMode(false);
    setAutomaticApprovalSettings(automaticApprovalSettingsPrevious);
  };

  return (
    <MyContext.Consumer>
      {(context) => (
        <React.Fragment>
          <Toolbar>
            <Typography variant="h6" id="tableTitle" component="div">
              {t("commuter_ticket")}
            </Typography>
            {!isAddTicketMode && !readOnly && <Add className="selector margin left-1 top-0 bottom-0" onClick={() => onToggleAddTicketMode(true)} />}
            {isAddTicketMode && <Cancel className="selector margin left-1 top-0 bottom-0" onClick={() => onToggleAddTicketMode(false)} />}
          </Toolbar>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  {ticketColumns
                    .filter((item) => !item.hidden)
                    .map((column, key) => (
                      <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                        {column.label}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {ticketRows &&
                  ticketRows.map((row, key) => {
                    return (
                      <TableRow hover tabIndex={-1} key={key}>
                        {ticketColumns
                          .filter((item) => !item.hidden)
                          .map((column) => {
                            if (column.id === "Actions") {
                              return (
                                <TableCell key={100} align="left">
                                  {!row.isEditMode && !isAddTicketMode && (
                                    <>
                                      <Edit className="selector margin top-0 bottom-0" onClick={() => onToggleTicketEditMode(row.id)} />
                                      {/* <Delete className="selector margin right-1 top-0 bottom-0" onClick={() => onDeleteTicket(row.id)} /> */}
                                      <Switch
                                        className="selector margin left-0 top-0 bottom-0"
                                        color="primary"
                                        disabled={readOnly}
                                        checked={row.Enabled}
                                        onChange={(e, checked) => {
                                          row.Enabled = checked;
                                          onSaveTicket(row.id);
                                        }}
                                      />
                                    </>
                                  )}
                                  {row.isEditMode && !isAddTicketMode && (
                                    <>
                                      <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveTicket(row.id)} />
                                      <Undo className="selector margin top-0 bottom-0" onClick={() => onRevert(row.id)} />
                                    </>
                                  )}
                                  {row.isAddMode && <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onAddTicket(row.id)} />}
                                </TableCell>
                              );
                            }
                            const value = row[column.id];
                            if (row.isAddMode || (row.isEditMode && column.editable)) {
                              if (column.id === "Rule") {
                                return (
                                  <TableCell key={column.id} align={column.align}>
                                    <MyNumberField
                                      name={column.id}
                                      label={t("reimbursement_for_percentage", { percent: 25 })}
                                      size="small"
                                      value={JSON.parse(value)[0].reimbursementSEK}
                                      onChange={(e) => onChangeTicketRule(row.id, column.id, value, 0, e.target.value)}
                                    />
                                    <MyNumberField
                                      name={column.id}
                                      label={t("reimbursement_for_percentage", { percent: 50 })}
                                      size="small"
                                      value={JSON.parse(value)[1].reimbursementSEK}
                                      onChange={(e) => onChangeTicketRule(row.id, column.id, value, 1, e.target.value)}
                                    />
                                    <MyNumberField
                                      name={column.id}
                                      label={t("reimbursement_for_percentage", { percent: 75 })}
                                      size="small"
                                      value={JSON.parse(value)[2].reimbursementSEK}
                                      onChange={(e) => onChangeTicketRule(row.id, column.id, value, 2, e.target.value)}
                                    />
                                    <MyNumberField
                                      name={column.id}
                                      label={t("reimbursement_for_percentage", { percent: 100 })}
                                      size="small"
                                      value={JSON.parse(value)[3].reimbursementSEK}
                                      onChange={(e) => onChangeTicketRule(row.id, column.id, value, 3, e.target.value)}
                                    />
                                  </TableCell>
                                );
                              }
                              return (
                                <TableCell key={column.id} align={column.align}>
                                  <MyTextField name={column.id} size="small" value={value} onChange={(value) => onChangeTicket(row.id, column.id, value)} />
                                </TableCell>
                              );
                            }
                            return (
                              <TableCell key={column.id} align={column.align}>
                                {column.format && typeof value === "number" ? column.format(value) : value}
                              </TableCell>
                            );
                          })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <Toolbar>
            <Typography variant="h6" id="tableTitle" component="div">
              {t("base_price")}
            </Typography>
            {!isAddBasePriceMode && !readOnly && <Add className="selector margin left-1 top-0 bottom-0" onClick={() => onToggleAddMode(true)} />}
            {isAddBasePriceMode && <Cancel className="selector margin left-1 top-0 bottom-0" onClick={() => onToggleAddMode(false)} />}
          </Toolbar>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  {basePriceColumns
                    .filter((item) => !item.hidden)
                    .map((column, key) => (
                      <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                        {column.label}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {basePriceRows &&
                  basePriceRows.map((row, key) => {
                    return (
                      <TableRow hover tabIndex={-1} key={key}>
                        {basePriceColumns
                          .filter((item) => !item.hidden)
                          .map((column) => {
                            if (column.id === "Actions") {
                              return (
                                <TableCell key={100} align="left">
                                  {!row.isEditMode && !isAddBasePriceMode && (
                                    <>
                                      <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onToggleBasePriceEditMode(row.id)} />
                                      <Delete className="selector margin top-0 bottom-0" onClick={() => onDeleteBasePrice(row.id)} />
                                    </>
                                  )}
                                  {row.isEditMode && !isAddBasePriceMode && (
                                    <>
                                      <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveBasePrice(row.id)} />
                                      <Undo className="selector margin top-0 bottom-0" onClick={() => onRevert(row.id)} />
                                    </>
                                  )}
                                  {row.isAddMode && <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onAddBasePrice(row.id)} />}
                                </TableCell>
                              );
                            }
                            const value = row[column.id];
                            if (row.isAddMode || (row.isEditMode && column.editable)) {
                              return (
                                <TableCell key={column.id} align={column.align}>
                                  <MyNumberField name={column.id} size="small" value={value} onChange={(e) => onChangeBasePirce(e, row)} />
                                </TableCell>
                              );
                            }
                            return (
                              <TableCell key={column.id} align={column.align}>
                                {column.format && typeof value === "number" ? column.format(value) : value}
                              </TableCell>
                            );
                          })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <Toolbar>
            <Typography variant="h6" id="tableTitle" component="div">
              {t("auto_claim_processing")}
            </Typography>
            <Switch
              className="margin left-1 top-0 bottom-0"
              color="primary"
              disabled={readOnly}
              checked={autoApproval}
              onChange={(e, checked) => onChangeAutoApprovalSettings(AutomaticApprovalSettings.Enable, checked.toString())}
            />
          </Toolbar>
          <TableContainer>
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  {autoApprovalColumns
                    .filter((item) => !item.hidden)
                    .map((column, key) => (
                      <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                        {column.label}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {automaticApprovalSettings &&
                  automaticApprovalSettings
                    .filter(
                      (item) =>
                        !item.Key.startsWith(AutomaticApprovalSettings.TrainNumbersToRemove) &&
                        !item.Key.startsWith(AutomaticApprovalSettings.StationsToRemove) &&
                        !item.Key.startsWith(AutomaticApprovalSettings.Periods) &&
                        [
                          AutomaticApprovalSettings.MaxClaimsIn24Hours,
                          AutomaticApprovalSettings.MaxClaimsDuring7Days,
                          AutomaticApprovalSettings.MaxClaimsDuring30Days,
                        ].indexOf(item.Key) <= -1
                    )
                    .map((row, key) => {
                      return (
                        <TableRow hover tabIndex={-1} key={key}>
                          {autoApprovalColumns
                            .filter((item) => !item.hidden)
                            .map((column) => {
                              if (column.id === "Actions") {
                                return (
                                  <TableCell key={100} align="left">
                                    {!row.isEditMode && (
                                      <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onToggleSettingsEditMode(row.id)} />
                                    )}
                                    {row.isEditMode && (
                                      <>
                                        <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveAutomaticApproval(row.id)} />
                                        <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertAutomaticApproval(row.id)} />
                                      </>
                                    )}
                                  </TableCell>
                                );
                              }
                              let value = row[column.id];
                              if ((row.isEditMode || row.isAddMode) && column.editable) {
                                if (row.DataType === DataType.Bool) {
                                  return (
                                    <TableCell key={column.id} align={column.align}>
                                      <MyTextField size="small" value={value} onChange={(value) => onChangeApprovalSettingRow(value, column.id, row)} />
                                    </TableCell>
                                  );
                                }
                                if (row.Key == AutomaticApprovalSettings.AllowedFileExtension) {
                                  return (
                                    <TableCell key={column.id} align={column.align}>
                                      <MySelect
                                        size="small"
                                        style={{ width: "15rem" }}
                                        label={t("status")}
                                        onChange={(values) => onChangeApprovalSettingRow(values.map((item) => item.value).toString(), column.id, row)}
                                        options={AllowedFileExtensions}
                                        value={value.split(",").map((item) => ({ key: item, value: item }))}
                                      />
                                    </TableCell>
                                  );
                                }
                                return (
                                  <TableCell key={column.id} align={column.align}>
                                    <MyNumberField
                                      decimalSeparator={row.DataType === DataType.Decimal ? "," : false}
                                      size="small"
                                      value={value.replace(".", ",")}
                                      onChange={(e) => onChangeApprovalSettingRow(e.target.value.replace(",", "."), column.id, row)}
                                    />
                                  </TableCell>
                                );
                              }
                              return (
                                <TableCell key={column.id} align={column.align}>
                                  {value.replace(".", ",")}
                                </TableCell>
                              );
                            })}
                        </TableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
          <Toolbar>
            <Typography variant="h6" id="tableTitle" component="div">
              {t("trains_to_remove")}
            </Typography>
            {!isAddTrainsToRemoveMode && !readOnly && (
              <Add className="selector margin left-1 top-0 bottom-0" onClick={() => onToggleTrainNumbersToRemoveAddMode()} />
            )}
            {isAddTrainsToRemoveMode && <Cancel className="selector margin left-1 top-0 bottom-0" onClick={() => onRevertAutomaticApproval()} />}
          </Toolbar>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  {trainsToRemoveColumns
                    .filter((item) => !item.hidden)
                    .map((column, key) => (
                      <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                        {column.label}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {automaticApprovalSettings &&
                  automaticApprovalSettings
                    .filter((item) => item.Key.startsWith(AutomaticApprovalSettings.TrainNumbersToRemove))
                    .map((row, key) => {
                      return (
                        <TableRow hover tabIndex={-1} key={key}>
                          {trainsToRemoveColumns
                            .filter((item) => !item.hidden)
                            .map((column) => {
                              if (column.id === "Actions") {
                                return (
                                  <TableCell key={100} align="left">
                                    {!row.isEditMode && !isAddTrainsToRemoveMode && (
                                      <>
                                        <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onToggleSettingsEditMode(row.id)} />
                                        <Delete className="selector margin top-0 bottom-0" onClick={() => onDeleteAutoApprovalSetting(row.id)} />
                                      </>
                                    )}
                                    {row.isEditMode && !isAddTrainsToRemoveMode && (
                                      <>
                                        <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveAutomaticApproval(row.id)} />
                                        <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertAutomaticApproval(row.id)} />
                                      </>
                                    )}
                                    {row.isAddMode && (
                                      <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onAddAutoApprovalSettings(row)} />
                                    )}
                                  </TableCell>
                                );
                              }
                              const value = row[column.id];
                              return (
                                <TableCell key={column.id} align={column.align}>
                                  {(row.isEditMode || row.isAddMode) && column.editable ? (
                                    <>
                                      {column.type === "number" && (
                                        <MyNumberField
                                          size="small"
                                          value={value}
                                          onChange={(e) => onChangeApprovalSettingRow(e.target.value, column.id, row)}
                                        />
                                      )}
                                      {column.type === "date" && (
                                        <MyDatePicker value={value} onAccept={(date) => onChangeApprovalSettingRow(date.toString(), column.id, row)} />
                                      )}
                                    </>
                                  ) : column.format ? (
                                    column.format(value)
                                  ) : (
                                    value
                                  )}
                                </TableCell>
                              );
                            })}
                        </TableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
          <Toolbar>
            <Typography variant="h6" id="tableTitle" component="div">
              {t("stations_to_remove")}
            </Typography>
            {!isAddStationsToRemoveMode && !readOnly && (
              <Add className="selector margin left-1 top-0 bottom-0" onClick={() => onToggleStationsToRemoveAddMode()} />
            )}
            {isAddStationsToRemoveMode && <Cancel className="selector margin left-1 top-0 bottom-0" onClick={() => onRevertAutomaticApproval()} />}
          </Toolbar>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  {stationsToRemoveColumns
                    .filter((item) => !item.hidden)
                    .map((column, key) => (
                      <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                        {column.label}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {automaticApprovalSettings &&
                  automaticApprovalSettings
                    .filter((item) => item.Key.startsWith(AutomaticApprovalSettings.StationsToRemove))
                    .map((row, key) => {
                      return (
                        <TableRow hover tabIndex={-1} key={key}>
                          {stationsToRemoveColumns
                            .filter((item) => !item.hidden)
                            .map((column) => {
                              if (column.id === "Actions") {
                                return (
                                  <TableCell key={100} align="left">
                                    {!row.isEditMode && !isAddStationsToRemoveMode && (
                                      <>
                                        <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onToggleSettingsEditMode(row.id)} />
                                        <Delete className="selector margin top-0 bottom-0" onClick={() => onDeleteAutoApprovalSetting(row.id)} />
                                      </>
                                    )}
                                    {row.isEditMode && !isAddStationsToRemoveMode && (
                                      <>
                                        <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveAutomaticApproval(row.id)} />
                                        <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertAutomaticApproval(row.id)} />
                                      </>
                                    )}
                                    {row.isAddMode && (
                                      <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onAddAutoApprovalSettings(row)} />
                                    )}
                                  </TableCell>
                                );
                              }
                              const value = row[column.id];
                              return (
                                <TableCell key={column.id} align={column.align}>
                                  {(row.isEditMode || row.isAddMode) && column.editable ? (
                                    <>
                                      {column.type !== "date" && (
                                        <MyDropdown
                                          onChange={(e, value) => onChangeApprovalSettingRow(value, column.id, row)}
                                          value={value?.code ? value : getTrainStationOptions(context.trainStations).find((option) => option.code === value)}
                                          options={getTrainStationOptions(context.trainStations)}
                                        />
                                      )}
                                      {column.type === "date" && (
                                        <MyDatePicker value={value} onAccept={(date) => onChangeApprovalSettingRow(date.toString(), column.id, row)} />
                                      )}
                                    </>
                                  ) : column.format ? (
                                    column.format(value)
                                  ) : (
                                    value
                                  )}
                                </TableCell>
                              );
                            })}
                        </TableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
          <Toolbar>
            <Typography variant="h6" id="tableTitle" component="div">
              {t("inactive_periods")}
            </Typography>
            {!isAddPeriodsMode && !readOnly && <Add className="selector margin left-1 top-0 bottom-0" onClick={() => onTogglePeriodsAddMode()} />}
            {isAddPeriodsMode && <Cancel className="selector margin left-1 top-0 bottom-0" onClick={() => onRevertAutomaticApproval()} />}
          </Toolbar>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  {periodsColumns
                    .filter((item) => !item.hidden)
                    .map((column, key) => (
                      <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                        {column.label}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {automaticApprovalSettings &&
                  automaticApprovalSettings
                    .filter((item) => item.Key.startsWith(AutomaticApprovalSettings.Periods))
                    .map((row, key) => {
                      return (
                        <TableRow hover tabIndex={-1} key={key}>
                          {periodsColumns
                            .filter((item) => !item.hidden)
                            .map((column) => {
                              if (column.id === "Actions") {
                                return (
                                  <TableCell key={100} align="left">
                                    {!row.isEditMode && !isAddPeriodsMode && (
                                      <>
                                        <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onToggleSettingsEditMode(row.id)} />
                                        <Delete className="selector margin top-0 bottom-0" onClick={() => onDeleteAutoApprovalSetting(row.id)} />
                                      </>
                                    )}
                                    {row.isEditMode && !isAddPeriodsMode && (
                                      <>
                                        <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveAutomaticApproval(row.id)} />
                                        <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertAutomaticApproval(row.id)} />
                                      </>
                                    )}
                                    {row.isAddMode && (
                                      <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onAddAutoApprovalSettings(row)} />
                                    )}
                                  </TableCell>
                                );
                              }
                              const value = row[column.id];
                              return (
                                <TableCell key={column.id} align={column.align}>
                                  {(row.isEditMode || row.isAddMode) && column.editable ? (
                                    <>
                                      {column.type === "date" && (
                                        <MyDateTimePicker value={value} onAccept={(date) => onChangeApprovalSettingRow(date.toString(), column.id, row)} />
                                      )}
                                    </>
                                  ) : column.format ? (
                                    column.format(value)
                                  ) : (
                                    value
                                  )}
                                </TableCell>
                              );
                            })}
                        </TableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
          <Toolbar>
            <Typography variant="h6" id="tableTitle" component="div">
              {t("fraud_notification")}
            </Typography>
          </Toolbar>
          <TableContainer sx={{ maxHeight: 440 }} className="margin bottom-7">
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  {autoApprovalColumns
                    .filter((item) => !item.hidden)
                    .map((column, key) => (
                      <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                        {column.label}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {automaticApprovalSettings &&
                  [
                    automaticApprovalSettings.find((item) => item.Key === AutomaticApprovalSettings.MaxClaimsIn24Hours),
                    automaticApprovalSettings.find((item) => item.Key === AutomaticApprovalSettings.MaxClaimsDuring7Days),
                    automaticApprovalSettings.find((item) => item.Key === AutomaticApprovalSettings.MaxClaimsDuring30Days),
                  ].map((row, key) => {
                    return (
                      <TableRow hover tabIndex={-1} key={key}>
                        {autoApprovalColumns
                          .filter((item) => !item.hidden)
                          .map((column) => {
                            if (column.id === "Actions") {
                              return (
                                <TableCell key={100} align="left">
                                  {!row.isEditMode && !row.isAddMode && (
                                    <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onToggleSettingsEditMode(row.id)} />
                                  )}
                                  {row.isEditMode && (
                                    <>
                                      <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveAutomaticApproval(row.id)} />
                                      <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertAutomaticApproval(row.id)} />
                                    </>
                                  )}
                                  {row.isAddMode && (
                                    <>
                                      <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onAddAutomaticApproval(row.id)} />
                                      <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertAutomaticApproval(row.id)} />
                                    </>
                                  )}
                                </TableCell>
                              );
                            }
                            let value = row[column.id];
                            if ((row.isEditMode || row.isAddMode) && column.editable) {
                              return (
                                <TableCell key={column.id} align={column.align}>
                                  <MyNumberField size="small" value={value} onChange={(e) => onChangeApprovalSettingRow(e.target.value, column.id, row)} />
                                </TableCell>
                              );
                            }
                            return (
                              <TableCell key={column.id} align={column.align}>
                                {value}
                              </TableCell>
                            );
                          })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </React.Fragment>
      )}
    </MyContext.Consumer>
  );
};
